import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { AppInfoService } from '../../services/app-info.service';
import { DeviceInfoService } from '../../services/device-info.service';
import { TelemetryIdentityService } from './telemetry-identity.service';
import { AuthSessionService } from '../../services/auth/auth-session.service';

export interface TelemetrySnapshot {
  readonly properties: Readonly<Record<string, string>>;
  readonly version?: string;
  readonly operatingSystem?: string;
  readonly model?: string;
}

/** Composes immutable occurrence/operation context from published owners; never reads credentials or sends data. */
@Injectable({ providedIn: 'root' })
export class TelemetryContextService {
  readonly accountChanges$ = this.sessions.identity$;
  private diagnostic?: Readonly<{ id: string; expiry: number }>;

  constructor(
    private identity: TelemetryIdentityService,
    private app: AppInfoService,
    private device: DeviceInfoService,
    private sessions: AuthSessionService
  ) {}

  publishDiagnostic(id?: string, expiry = 0): void {
    this.diagnostic = id ? Object.freeze({ id, expiry }) : undefined;
  }

  capture(): TelemetrySnapshot {
    const platform = Capacitor.getPlatform();
    const native = platform !== 'web';
    const properties: Record<string, string> = { AppRunId: this.identity.appRunId, Platform: platform };
    const unavailable: Record<string, string> = {};
    const add = (name: string, value: string | undefined, reason: string) => {
      if (value) {
        properties[name] = value;
      } else {
        unavailable[name] = reason;
      }
    };
    add('InstallationId', this.identity.installationId, this.identity.availability);
    add('AppBuild', this.app.build, this.app.availability === 'pending' ? 'pending' : 'failed');
    if (this.app.version) {
      properties.InputType = this.device.inputType;
    } else {
      unavailable.InputType = this.app.availability === 'pending' ? 'pending' : 'failed';
      unavailable.application_Version = unavailable.InputType;
    }
    const info = this.device.deviceInfo;
    add('OSVersion', native ? info?.osVersion : undefined, native ? (this.device.availability === 'pending' ? 'pending' : 'failed') : 'unsupported');
    if (native && !info?.model) {
      unavailable.client_Model = this.device.availability === 'pending' ? 'pending' : 'failed';
    }
    if (native && !info?.operatingSystem) {
      unavailable.client_OS = this.device.availability === 'pending' ? 'pending' : 'failed';
    }
    const account = this.sessions.state.identity;
    if (account) {
      properties.UserId = String(account.userId);
      add('LoginContextId', account.loginContextId, 'failed');
    }
    if (this.diagnostic && Date.now() < this.diagnostic.expiry) {
      properties.DebugSessionId = this.diagnostic.id;
    }
    if (Object.keys(unavailable).length) {
      properties.MetadataUnavailable = JSON.stringify(unavailable);
    }
    return Object.freeze({
      properties: Object.freeze(properties),
      version: this.app.version,
      operatingSystem: native ? info?.operatingSystem : undefined,
      model: native ? info?.model : undefined,
    });
  }
}